import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../api";

const GA4_REPORTS_URL = "https://analytics.google.com/analytics/web/#/a373675971p546822927/reports/snapshot";

const COLUMNS = [
  { key: "athlete", label: "Athlete" },
  { key: "conversions", label: "Conversions", num: true },
  { key: "visitors", label: "Human traffic", num: true },
  { key: "avg_time", label: "Avg time on site", num: true },
];

// 137.4 -> "2m 17s"; under a minute -> "17s".
function formatSeconds(s) {
  const total = Math.round(s || 0);
  const m = Math.floor(total / 60);
  const sec = total % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
}

function trendDirection(cur, prev) {
  if (cur > prev) return "up";
  if (cur < prev) return "down";
  return "flat";
}

const TREND_GLYPH = { up: "▲", down: "▼", flat: "–" };

function Trend({ cur, prev }) {
  const dir = trendDirection(cur, prev);
  return <span className={`an-trend ${dir}`}>{TREND_GLYPH[dir]}</span>;
}

function GA4Link({ className }) {
  return (
    <a href={GA4_REPORTS_URL} target="_blank" rel="noopener noreferrer" className={className}
      onClick={(e) => e.stopPropagation()} data-testid="an-ga4-link">
      Open in GA4 ↗
    </a>
  );
}

// "YYYY-MM-DD" in the browser's local calendar date — NOT toISOString(), which
// converts to UTC and shifts the date by a day in negative-UTC-offset timezones.
function localISODate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Default window: trailing 28 days ending today, in the browser's local date.
function defaultRange() {
  const until = new Date();
  const since = new Date(until);
  since.setDate(since.getDate() - 27);
  return { since: localISODate(since), until: localISODate(until) };
}

export default function AnalyticsView({ goHome }) {
  const initial = useMemo(defaultRange, []);
  const [since, setSince] = useState(initial.since);
  const [until, setUntil] = useState(initial.until);
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [rollupData, setRollupData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortKey, setSortKey] = useState("visitors");
  const [sortDir, setSortDir] = useState(-1);
  const [selectedSlug, setSelectedSlug] = useState(null);

  useEffect(() => {
    api.get("/sites").then(({ data }) => {
      setSites(Array.isArray(data) ? data : (data.items || []));
      setSitesLoading(false);
    }).catch(() => { setSitesLoading(false); });
  }, []);

  const rangeError = since && until && since > until ? "Start date must be before end date." : "";

  const loadRollup = useCallback(() => {
    if (rangeError) return;
    setLoading(true);
    setError("");
    api.get("/analytics/rollup", { params: { since, until } })
      .then(({ data }) => { setRollupData(data); setLoading(false); })
      .catch((err) => {
        setError(formatApiErrorDetail(err?.response?.data?.detail));
        setLoading(false);
      });
  }, [since, until, rangeError]);

  useEffect(() => { loadRollup(); }, [loadRollup]);

  const liveSites = useMemo(() => sites.filter((s) => s.status === "live"), [sites]);

  const statsBySlug = useMemo(() => {
    const map = {};
    (rollupData?.per_site || []).forEach((p) => { map[p.slug] = p; });
    return map;
  }, [rollupData]);

  const rows = useMemo(() => liveSites.map((s) => {
    const st = statsBySlug[s.slug] || {};
    const ga4 = st.ga4 || {};
    return {
      slug: s.slug,
      athlete: s.athlete_name || s.slug,
      conversions: st.bookings_total ?? 0,
      conversions_prev: st.bookings_total_prev ?? 0,
      visitors: ga4.active_users ?? 0,
      visitors_prev: ga4.active_users_prev ?? 0,
      avg_time: ga4.avg_engagement_seconds ?? 0,
      avg_time_prev: ga4.avg_engagement_seconds_prev ?? 0,
    };
  }), [liveSites, statsBySlug]);

  const sortedRows = useMemo(() => [...rows].sort((a, b) => {
    const x = a[sortKey], y = b[sortKey];
    if (typeof x === "number" || typeof y === "number") return ((x || 0) - (y || 0)) * sortDir;
    return String(x ?? "").localeCompare(String(y ?? "")) * sortDir;
  }), [rows, sortKey, sortDir]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir((d) => -d);
    else { setSortKey(k); setSortDir(-1); }
  };

  const selectedRow = selectedSlug ? rows.find((r) => r.slug === selectedSlug) : null;

  // Gate on the FIRST load only — once we have data, a date-range change keeps
  // showing the last-good numbers in the background rather than blanking the page.
  if (sitesLoading || (loading && !rollupData)) {
    return (
      <div className="view" data-testid="analytics-view">
        <div className="crumb">
          <a onClick={goHome} data-testid="analytics-back-home">← Home</a>
          {" / "}
          Analytics
        </div>
        <div className="disc-empty">Loading…</div>
      </div>
    );
  }

  return (
    <div className="view" data-testid="analytics-view">
      <div className="crumb">
        <a onClick={goHome} data-testid="analytics-back-home">← Home</a>
        {" / "}
        Analytics
      </div>

      {selectedRow ? (
        <>
          <a className="an-back" onClick={() => setSelectedSlug(null)} data-testid="an-back-to-leaderboard">
            ← Back to leaderboard
          </a>
          <div className="sec-head">{selectedRow.athlete}</div>
          <div className="sec-desc">{since} – {until}</div>
          <div className="an-kpis">
            <div className="stat">
              <div className="stat-label">Conversions</div>
              <div className="stat-num">{selectedRow.conversions}
                <Trend cur={selectedRow.conversions} prev={selectedRow.conversions_prev} />
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Human traffic</div>
              <div className="stat-num">{selectedRow.visitors}
                <Trend cur={selectedRow.visitors} prev={selectedRow.visitors_prev} />
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Avg time on site</div>
              <div className="stat-num">{formatSeconds(selectedRow.avg_time)}
                <Trend cur={selectedRow.avg_time} prev={selectedRow.avg_time_prev} />
              </div>
            </div>
          </div>
          <GA4Link className="an-back" />
        </>
      ) : (
        <>
          <div className="sec-head">Analytics</div>
          <div className="sec-desc">Traffic and conversions across all live athlete sites.</div>

          <div className="an-daterow">
            <label htmlFor="an-since">From</label>
            <input id="an-since" type="date" value={since} max={until}
              onChange={(e) => setSince(e.target.value)} data-testid="an-since" />
            <label htmlFor="an-until">To</label>
            <input id="an-until" type="date" value={until} min={since}
              onChange={(e) => setUntil(e.target.value)} data-testid="an-until" />
            {rangeError && <span className="an-error" data-testid="an-range-error">{rangeError}</span>}
          </div>

          {error && <div className="an-error" data-testid="an-error">{error}</div>}

          <div className="an-kpis">
            <div className="stat">
              <div className="stat-label">Conversions</div>
              <div className="stat-num">{rollupData?.bookings_total ?? 0}
                <Trend cur={rollupData?.bookings_total ?? 0} prev={rollupData?.bookings_total_prev ?? 0} />
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Human traffic</div>
              <div className="stat-num">{rollupData?.ga4_active_users ?? 0}
                <Trend cur={rollupData?.ga4_active_users ?? 0} prev={rollupData?.ga4_active_users_prev ?? 0} />
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Avg time on site</div>
              <div className="stat-num">{formatSeconds(rollupData?.ga4_avg_engagement_seconds)}
                <Trend cur={rollupData?.ga4_avg_engagement_seconds ?? 0}
                  prev={rollupData?.ga4_avg_engagement_seconds_prev ?? 0} />
              </div>
            </div>
            <div className="stat">
              <div className="stat-label">Live sites</div>
              <div className="stat-num">{liveSites.length}</div>
            </div>
          </div>

          <div className="an-table-wrap">
            <table className="an-table" data-testid="an-table">
              <thead>
                <tr>
                  {COLUMNS.map((c) => (
                    <th key={c.key} onClick={() => toggleSort(c.key)}>
                      {c.label}{sortKey === c.key ? (sortDir < 0 ? " ▼" : " ▲") : ""}
                    </th>
                  ))}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && sortedRows.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 16, color: "var(--text-faint)" }}>Loading…</td></tr>
                )}
                {!loading && sortedRows.length === 0 && (
                  <tr><td colSpan={5} style={{ padding: 16, color: "var(--text-faint)" }}>No live sites yet.</td></tr>
                )}
                {sortedRows.map((r) => (
                  <tr key={r.slug} onClick={() => setSelectedSlug(r.slug)} data-testid={`an-row-${r.slug}`}>
                    <td>{r.athlete}</td>
                    <td>{r.conversions} <Trend cur={r.conversions} prev={r.conversions_prev} /></td>
                    <td>{r.visitors} <Trend cur={r.visitors} prev={r.visitors_prev} /></td>
                    <td>{formatSeconds(r.avg_time)} <Trend cur={r.avg_time} prev={r.avg_time_prev} /></td>
                    <td><GA4Link /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
